

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRoles,
  fetchPermissions,
  assignRolePermissions,
  fetchRolePermissions,
} from "../redux/actions/roleActions";
import {
  FiShield,
  FiKey,
  FiCheck,
  FiArrowLeft,
  FiUsers,
  FiLock,
  FiAlertCircle,
  FiRefreshCw,
  FiSave
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const AssignPermissionToRole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roles, permissions, rolePermissions, loading, error, success } = useSelector(
    (state) => state.role
  );

  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (selectedRole) {
      dispatch(fetchRolePermissions(selectedRole));
    } else {
      setSelectedPermissions([]);
    }
  }, [dispatch, selectedRole]);

  useEffect(() => {
    if (rolePermissions?.length && selectedRole) {
      const perms = rolePermissions
        .filter((p) => p.role_id === parseInt(selectedRole))
        .map((p) => p.permission_id);
      setSelectedPermissions(perms);
    }
  }, [rolePermissions, selectedRole]);

  const handlePermissionToggle = (permId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId)
        ? prev.filter((id) => id !== permId)
        : [...prev, permId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPermissions.length === filteredPermissions.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(filteredPermissions.map(perm => perm.id));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRole) {
      dispatch(
        assignRolePermissions({
          role_id: selectedRole,
          permissionIds: selectedPermissions,
        })
      );
    }
  };

  // Filter permissions based on search term
  const filteredPermissions = permissions.filter(perm =>
    perm.permission_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    perm.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected role name
  const selectedRoleName = roles.find(role => role.id === parseInt(selectedRole))?.role_name;

  // Group permissions by module
  const groupedPermissions = filteredPermissions.reduce((groups, perm) => {
    const module = perm.permission_name?.split('.')[0] || 'other';
    if (!groups[module]) {
      groups[module] = [];
    }
    groups[module].push(perm);
    return groups;
  }, {});

  if (loading && !selectedRole) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        {/* <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/roles")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Roles
          </button>
        </div> */}

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiShield className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Assign Permissions to Role
                </h1>
                <p className="text-purple-100">
                  Manage role-based access control by assigning permissions to roles
                </p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Permissions assigned successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The permissions have been updated for the selected role.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof error === "string" ? error : error?.message || "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check your selection and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div className="flex items-center gap-2">
                    <FiUsers className="text-sm" />
                    Select Role <span className="text-red-500">*</span>
                  </div>
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  required
                  className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 transition"
                  disabled={loading}
                >
                  <option value="">-- Choose a Role --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Select a role to manage its permissions
                </p>
              </div>

              {/* Permissions Section */}
              {selectedRole && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <FiKey className="text-sm" />
                        Permissions
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          ({selectedPermissions.length} selected)
                        </span>
                      </div>
                    </label>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="text-xs bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded-lg transition-colors"
                      >
                        {selectedPermissions.length === filteredPermissions.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <button
                        type="button"
                        onClick={() => dispatch(fetchPermissions())}
                        disabled={loading}
                        className="text-xs bg-gray-500 hover:bg-gray-600 text-white py-1 px-3 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <FiRefreshCw className={`inline mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                  </div>

                  {/* Search Box */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-2 px-4 pl-10 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    <FiKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                  </div>

                  {/* Permissions List */}
                  <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-xl p-4 max-h-96 overflow-y-auto">
                    {filteredPermissions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FiKey className="text-3xl mx-auto mb-2 opacity-50" />
                        <p>No permissions found</p>
                        <p className="text-sm">Try adjusting your search terms</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
                          <div key={module} className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize border-b border-gray-200 dark:border-gray-600 pb-1">
                              {module} Module
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {modulePermissions.map((perm) => (
                                <label
                                  key={perm.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                                    selectedPermissions.includes(perm.id)
                                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissions.includes(perm.id)}
                                    onChange={() => handlePermissionToggle(perm.id)}
                                    className="mt-1 text-purple-500 focus:ring-purple-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {perm.permission_name}
                                    </div>
                                    {perm.description && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {perm.description}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRole("");
                    setSelectedPermissions([]);
                    setSearchTerm("");
                  }}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Selection
                </button>
                
                <button
                  type="submit"
                  disabled={loading || !selectedRole}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Assigning Permissions...
                    </>
                  ) : (
                    <>
                      <FiSave className="text-lg" />
                      Assign Permissions to {selectedRoleName || 'Role'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Information Card */}
        <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">
            💡 Role-Permission Management
          </h3>
          <ul className="text-purple-800 dark:text-purple-200 text-sm space-y-2">
            <li>• Select a role to view and modify its assigned permissions</li>
            <li>• Permissions are grouped by modules for better organization</li>
            <li>• Use the search feature to quickly find specific permissions</li>
            <li>• Select/Deselect all permissions using the toggle buttons</li>
            <li>• Changes are saved immediately when you assign permissions</li>
            <li>• Each role can have multiple permissions assigned</li>
            <li>• Users inherit permissions through their assigned roles</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssignPermissionToRole;